/**
 * Version diff (doc 147 §6 "Branch Compare", §9 Phase 4).
 *
 * Pick two commit SHAs, see what changed in the model between them.
 * Presentational — the diff itself comes from the injected `onRunDiff`.
 */
import { useState } from 'react';
import type { ManifestDiff } from '../state/atlasApiTypes.js';
import { navigate } from '../state/hashRouter.js';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { EmptyState } from '../components/EmptyState.js';

export interface VersionDiffViewProps {
  /** Fetches the diff between two snapshots; rejects on failure. */
  onRunDiff: (fromSha: string, toSha: string) => Promise<ManifestDiff>;
}

export function VersionDiffView({ onRunDiff }: VersionDiffViewProps): React.JSX.Element {
  const [fromSha, setFromSha] = useState('');
  const [toSha, setToSha] = useState('');
  const [diff, setDiff] = useState<ManifestDiff | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    if (!fromSha.trim() || !toSha.trim()) {
      setError('Enter both commit SHAs.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      setDiff(await onRunDiff(fromSha.trim(), toSha.trim()));
      setStatus('idle');
    } catch {
      setError('Could not diff those snapshots — check the commit SHAs exist.');
      setStatus('error');
    }
  };

  return (
    <article>
      <div className="entity-head">
        <Icon name="difference" size="lg" label="Version diff" />
        <h1 className="entity-title">Version diff</h1>
      </div>

      <div className="filter-bar" style={{ marginBottom: 'var(--sp-lg)' }}>
        <input
          className="filter-chip"
          aria-label="From commit SHA"
          placeholder="from SHA"
          value={fromSha}
          onChange={(e) => setFromSha(e.target.value)}
        />
        <Icon name="arrow_forward" size="sm" />
        <input
          className="filter-chip"
          aria-label="To commit SHA"
          placeholder="to SHA"
          value={toSha}
          onChange={(e) => setToSha(e.target.value)}
        />
        <button className="btn btn-primary" onClick={run} disabled={status === 'loading'}>
          {status === 'loading' ? 'Diffing…' : 'Run diff'}
        </button>
        {status === 'loading' && <span role="status" aria-label="Running diff" className="spinner" />}
      </div>

      {error && <ErrorState variant="inline" message={error} />}

      {diff && (
        <>
          <div className="card" style={{ marginBottom: 'var(--sp-lg)' }}>
            <strong style={{ color: 'var(--good)' }}>{diff.summary.added}</strong> added ·{' '}
            <strong style={{ color: 'var(--danger)' }}>{diff.summary.removed}</strong> removed ·{' '}
            <strong style={{ color: 'var(--caution)' }}>{diff.summary.changed}</strong> changed
          </div>

          {diff.addedEntities.length === 0 &&
          diff.removedEntities.length === 0 &&
          diff.changedEntities.length === 0 ? (
            <EmptyState icon="check_circle" title="No model changes between those commits" />
          ) : (
            <>
              {diff.addedEntities.length > 0 && (
                <section style={{ marginBottom: 'var(--sp-md)' }}>
                  <div className="section-label" style={{ color: 'var(--good)' }}>
                    Added · {diff.addedEntities.length}
                  </div>
                  {diff.addedEntities.map((id) => (
                    <button
                      key={id}
                      className="ref-row"
                      onClick={() => navigate({ kind: 'entity', id })}
                    >
                      <Icon name="add_circle" size="sm" color="var(--good)" />
                      <span className="mono">{id}</span>
                    </button>
                  ))}
                </section>
              )}
              {diff.removedEntities.length > 0 && (
                <section style={{ marginBottom: 'var(--sp-md)' }}>
                  <div className="section-label" style={{ color: 'var(--danger)' }}>
                    Removed · {diff.removedEntities.length}
                  </div>
                  {diff.removedEntities.map((id) => (
                    <button
                      key={id}
                      className="ref-row"
                      onClick={() => navigate({ kind: 'entity', id })}
                    >
                      <Icon name="remove_circle" size="sm" color="var(--danger)" />
                      <span className="mono">{id}</span>
                    </button>
                  ))}
                </section>
              )}
              {diff.changedEntities.length > 0 && (
                <section>
                  <div className="section-label" style={{ color: 'var(--caution)' }}>
                    Changed · {diff.changedEntities.length}
                  </div>
                  {diff.changedEntities.map((entity) => (
                    <div key={entity.id} className="card" style={{ marginBottom: 'var(--sp-sm)' }}>
                      <button
                        className="mono"
                        style={{ color: 'var(--text)', fontWeight: 700 }}
                        onClick={() => navigate({ kind: 'entity', id: entity.id })}
                      >
                        {entity.id}
                      </button>
                      <ul style={{ marginTop: 'var(--sp-xs)' }}>
                        {entity.fieldChanges.map((fc) => {
                          const detail =
                            fc.from !== undefined && fc.to !== undefined
                              ? ` (${fc.from} → ${fc.to})`
                              : fc.to !== undefined
                                ? ` (${fc.to})`
                                : fc.from !== undefined
                                  ? ` (${fc.from})`
                                  : '';
                          return (
                            <li
                              key={`${fc.field}-${fc.change}`}
                              className="mono"
                              style={{ fontSize: 'var(--fs-xs)' }}
                            >
                              {`${fc.field}: ${fc.change}${detail}`}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </section>
              )}
            </>
          )}
        </>
      )}
    </article>
  );
}
